import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _kebabCase from "lodash/kebabCase";
import _isUndefined from "lodash/isUndefined";
import _isEmpty from "lodash/isEmpty";

var _DRIVER_GROUP_CONFIG;

import "core-js/modules/es6.array.map.js";
import React, { useContext } from 'react';
import { ImpressionContext } from '@nerdwallet/analytics/react/impression';
import classes from '@nerdwallet/base-styles/classes'; // @todo: port into nwjs-marketplace

import { getMarketingBullets, getTermsAndFeesLink } from '@nerdwallet/cc-utils/lib/offer-api/selectors';
import { getSlug, isActive } from '@nerdwallet/marketplace/selectors/common';
import { getBalanceTransferIntroTime, getBalanceTransferIntroApr, getImageDimensions, getMatchScore, getImageRawDimensions } from '@nerdwallet/marketplace/selectors/credit-cards';
import { isNonMonetizing } from '@nerdwallet/marketplace/utils';
import { Li, List } from '@nerdwallet/currency';
import { mappings as quickFactMappings } from '@nerdwallet/react-quick-facts';
import MarketplaceApplyNow, { APPLY_NOW_LINK_TYPES, LINK_TYPES, APPLY_NOW_FINISH_TYPES } from '@nerdwallet/react-marketplace-apply-now';
import { findOurTakeHighlightContent } from '@nerdwallet/react-our-take-highlight';
import { DEFAULT_DRIVER_GROUP, getDrivers, getImageData, getLazyLoadableImageData as getLoadableImageData, getMonetizable, sanitizeNode, getLoaTooltipContent } from '../lib/utils';
import LikelihoodOfApprovalCTA from '../likelihood-of-approval-cta';
import styles from "../marketplace-product-card.module.css";
import { GET_YOUR_FREE_CREDIT_SCORE, LOA_CTA_TOOLTIP_LABEL, SEE_YOUR_APPROVAL_ODDS, TRK_PRODUCT_LOCATION } from '../lib/constants';
import { CreditScoreWidget } from '../creditscore_pill/CreditScoreWidget';
/**
 * Export all the keys of overridable driverGroups
 */

export var DRIVER_GROUPS_CREDIT_CARDS = {
  CC_BALANCE_TRANSFER: 'CC_BALANCE_TRANSFER',
  CC_BUILD_CREDIT: 'CC_BUILD_CREDIT',
  CC_CLASSIC_DEFAULT: 'CC_CLASSIC_DEFAULT',
  CC_REWARDS: 'CC_REWARDS',
  CC_BALANCE_TRANSFER_LOA: 'CC_BALANCE_TRANSFER_LOA',
  CC_BALANCE_TRANSFER_ROUNDUP: 'CC_BALANCE_TRANSFER_ROUNDUP',
  // Specific to SHOP-11146
  CC_BT_AND_REWARDS: 'CC_BT_AND_REWARDS',
  CC_BT_ROUNDUP_AND_REWARDS: 'CC_BT_ROUNDUP_AND_REWARDS',
  // Specific to SHOP-11741
  CC_REWARDS_11741_VARIANT: 'CC_REWARDS_11741_VARIANT',
  // Specific to SHOP-11606
  CC_BONUS_OFFER_11606_VARIANT: 'CC_BONUS_OFFER_11606_VARIANT',
  //  Specif to CCSH-1516
  CC_CREDIT_SCORE_PILL: 'CC_CREDIT_SCORE_PILL'
};
/**
 * @param {Object} args -
 * @param {Object} args.identity -
 * @param {Object} args.marketplaceEntity -
 * @param {string} args.trkProductPosition -
 * @returns {Object} - Quick fact object for LOA.
 */

var getQuickFactLikelihoodOfApproval = function getQuickFactLikelihoodOfApproval(args) {
  var _args$analytics, _args$analytics2;

  var loaTooltipContent = getLoaTooltipContent(args === null || args === void 0 ? void 0 : args.identity);
  var quickFactLikelihoodOfApprovalOptions = {
    ctaComponent: /*#__PURE__*/React.createElement(LikelihoodOfApprovalCTA, {
      approvalOddsEnabled: args.approvalOddsEnabled,
      analytics: (_args$analytics = args.analytics) === null || _args$analytics === void 0 ? void 0 : _args$analytics.loaCtaProps,
      ctaText: args.approvalOddsEnabled ? SEE_YOUR_APPROVAL_ODDS : GET_YOUR_FREE_CREDIT_SCORE,
      enableAnalytics: args.enableAnalytics,
      tooltipLabel: !_isEmpty(loaTooltipContent) ? LOA_CTA_TOOLTIP_LABEL : null,
      tooltipContent: loaTooltipContent
    }),
    identity: args.identity,
    trackingProps: {
      impressionKey: getSlug(args.marketplaceEntity),
      sectionCategory: 'product-card-approval-odds',
      // Element Interactions do not support productPosition, so use sectionPosition
      sectionPosition: args.trkProductPosition
    },
    approvalOddsEnabled: args.approvalOddsEnabled,
    analytics: (_args$analytics2 = args.analytics) === null || _args$analytics2 === void 0 ? void 0 : _args$analytics2.loaCtaProps
  };
  var quickFactsLikelihoodOfApproval;

  if (args.showMatchScoreCTA) {
    quickFactsLikelihoodOfApproval = quickFactMappings.creditCards.getQuickFactRecommendedCreditScoreRange(args.marketplaceEntity);
  } else if (args.isKetoCard) {
    var _args$analytics3;

    quickFactsLikelihoodOfApproval = quickFactMappings.creditCards.getQuickFactLikelihoodOfApprovalText(args.marketplaceEntity, _objectSpread(_objectSpread({}, quickFactLikelihoodOfApprovalOptions), {}, {
      ctaComponent: /*#__PURE__*/React.createElement(LikelihoodOfApprovalCTA, {
        approvalOddsEnabled: args.approvalOddsEnabled,
        analytics: (_args$analytics3 = args.analytics) === null || _args$analytics3 === void 0 ? void 0 : _args$analytics3.loaCtaProps,
        ctaText: args.approvalOddsEnabled ? SEE_YOUR_APPROVAL_ODDS : GET_YOUR_FREE_CREDIT_SCORE,
        enableAnalytics: args.enableAnalytics,
        isKetoCard: true,
        tooltipLabel: !_isEmpty(loaTooltipContent) ? LOA_CTA_TOOLTIP_LABEL : null,
        tooltipContent: loaTooltipContent
      })
    }));
  } else {
    quickFactsLikelihoodOfApproval = quickFactMappings.creditCards.getQuickFactLikelihoodOfApproval(args.marketplaceEntity, quickFactLikelihoodOfApprovalOptions);
  }

  return quickFactsLikelihoodOfApproval;
};
/**
 * @param {Object} args -
 * @param {Object} args.identity -
 * @param {Object} args.marketplaceEntity -
 * @param {string} args.trkProductPosition -
 * @returns {Object[]} - list of quick-fact objects.
 */


var getClassicDefaultDrivers = function getClassicDefaultDrivers(args) {
  var _args$analytics4, _args$impressionConte;

  return [quickFactMappings.creditCards.getQuickFactAnnualFee(args.marketplaceEntity), quickFactMappings.creditCards.getQuickFactRegularApr(args.marketplaceEntity, false, false, _objectSpread(_objectSpread({}, args === null || args === void 0 ? void 0 : (_args$analytics4 = args.analytics) === null || _args$analytics4 === void 0 ? void 0 : _args$analytics4.productImpressionProps), {}, {
    productImpressionId: args === null || args === void 0 ? void 0 : (_args$impressionConte = args.impressionContext) === null || _args$impressionConte === void 0 ? void 0 : _args$impressionConte.impressionId
  })), quickFactMappings.creditCards.getQuickFactIntroApr(args.marketplaceEntity), getQuickFactLikelihoodOfApproval(args)];
};

var getRewardsDrivers = function getRewardsDrivers(args) {
  var qfMappings = quickFactMappings.creditCards;
  return [qfMappings.getQuickFactAnnualFee(args.marketplaceEntity), qfMappings.getQuickFactRewardRate(args.marketplaceEntity), qfMappings.getQuickFactIntroOffer(args.marketplaceEntity), getQuickFactLikelihoodOfApproval(args)];
};

var getNewCreditDriver = function getNewCreditDriver(args) {
  var qfMappings = quickFactMappings.creditCards;
  var likelihood = getQuickFactLikelihoodOfApproval(args);
  likelihood.value = /*#__PURE__*/React.createElement(CreditScoreWidget, {
    minFico: args.marketplaceEntity.details.min_fico,
    maxFico: args.marketplaceEntity.details.max_fico,
    limitedCreditHistory: args.marketplaceEntity.details.limited_credit_history
  });
  return [qfMappings.getQuickFactAnnualFee(args.marketplaceEntity), qfMappings.getQuickFactRewardRate(args.marketplaceEntity), qfMappings.getQuickFactIntroOffer(args.marketplaceEntity), likelihood];
};
/**
 * Get drivers for CC_REWARDS_11741_VARIANT driver group for the following
 * 3 card categories: REWARDS, TRAVEL, CASHBACK
 * https://nerdwallet.atlassian.net/browse/SHOP-11741
 */


var getRewards11741VariantDrivers = function getRewards11741VariantDrivers(args) {
  // Show the 5th and 6th driver only if the cards qualify under
  // zero-percent apr or have balance transfer offering
  // If BalanceTransferIntroApr is defined and BalanceTransferIntroTime is greater than 0
  var hasBalanceTransferOffer = !_isUndefined(getBalanceTransferIntroApr(args.marketplaceEntity)) && getBalanceTransferIntroTime(args.marketplaceEntity) > 0;

  if (hasBalanceTransferOffer) {
    var _args$analytics5, _args$impressionConte2;

    return [].concat(_toConsumableArray(getRewardsDrivers(args)), [quickFactMappings.creditCards.getQuickFactIntroApr(args.marketplaceEntity), quickFactMappings.creditCards.getQuickFactRegularApr(args.marketplaceEntity, false, false, _objectSpread(_objectSpread({}, args === null || args === void 0 ? void 0 : (_args$analytics5 = args.analytics) === null || _args$analytics5 === void 0 ? void 0 : _args$analytics5.productImpressionProps), {}, {
      productImpressionId: args === null || args === void 0 ? void 0 : (_args$impressionConte2 = args.impressionContext) === null || _args$impressionConte2 === void 0 ? void 0 : _args$impressionConte2.impressionId
    }))]);
  }

  return getRewardsDrivers(args);
};

var getBuildCreditDrivers = function getBuildCreditDrivers(_ref) {
  var marketplaceEntity = _ref.marketplaceEntity,
      analytics = _ref.analytics,
      impressionContext = _ref.impressionContext;
  return [quickFactMappings.creditCards.getQuickFactAnnualFee(marketplaceEntity), quickFactMappings.creditCards.getQuickFactRegularApr(marketplaceEntity, false, false, _objectSpread(_objectSpread({}, analytics === null || analytics === void 0 ? void 0 : analytics.productImpressionProps), {}, {
    productImpressionId: impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId
  })), quickFactMappings.creditCards.getQuickFactIntroApr(marketplaceEntity), quickFactMappings.creditCards.getQuickFactMinimumDepositDescription(marketplaceEntity)];
};

var getBalanceTransferWithLOADrivers = function getBalanceTransferWithLOADrivers(args) {
  var _args$analytics6, _args$impressionConte3;

  return [quickFactMappings.creditCards.getQuickFactBalanceTransferFeeDescription(args.marketplaceEntity), quickFactMappings.creditCards.getQuickFactIntroApr(args.marketplaceEntity), quickFactMappings.creditCards.getQuickFactRegularApr(args.marketplaceEntity, false, false, _objectSpread(_objectSpread({}, (_args$analytics6 = args.analytics) === null || _args$analytics6 === void 0 ? void 0 : _args$analytics6.productImpressionProps), {}, {
    productImpressionId: args === null || args === void 0 ? void 0 : (_args$impressionConte3 = args.impressionContext) === null || _args$impressionConte3 === void 0 ? void 0 : _args$impressionConte3.impressionId
  })), getQuickFactLikelihoodOfApproval(args)];
};
/**
 * Get drivers for CC_BALANCE_TRANSFER driver group
 */


var getBalanceTransferDrivers = function getBalanceTransferDrivers(_ref2) {
  var marketplaceEntity = _ref2.marketplaceEntity,
      analytics = _ref2.analytics,
      impressionContext = _ref2.impressionContext;
  return [quickFactMappings.creditCards.getQuickFactAnnualFee(marketplaceEntity), quickFactMappings.creditCards.getQuickFactRegularApr(marketplaceEntity, false, false, _objectSpread(_objectSpread({}, analytics === null || analytics === void 0 ? void 0 : analytics.productImpressionProps), {}, {
    productImpressionId: impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId
  })), quickFactMappings.creditCards.getQuickFactIntroApr(marketplaceEntity), quickFactMappings.creditCards.getQuickFactBalanceTransferFeeDescription(marketplaceEntity)];
};
/**
 * Get drivers for CC_BALANCE_TRANSFER_ROUNDUP driver group
 */


var getBalanceTransferRoundupDrivers = function getBalanceTransferRoundupDrivers(args) {
  var _args$analytics7, _args$impressionConte4;

  return [quickFactMappings.creditCards.getQuickFactAnnualFee(args.marketplaceEntity), quickFactMappings.creditCards.getQuickFactIntroApr(args.marketplaceEntity), quickFactMappings.creditCards.getQuickFactRegularApr(args.marketplaceEntity, false, false, _objectSpread(_objectSpread({}, args === null || args === void 0 ? void 0 : (_args$analytics7 = args.analytics) === null || _args$analytics7 === void 0 ? void 0 : _args$analytics7.productImpressionProps), {}, {
    productImpressionId: args === null || args === void 0 ? void 0 : (_args$impressionConte4 = args.impressionContext) === null || _args$impressionConte4 === void 0 ? void 0 : _args$impressionConte4.impressionId
  })), getQuickFactLikelihoodOfApproval(args)];
};
/**
 * Groups Rewards Rate and Intro Offer driver together
 */


var getRewardRateAndIntroDriver = function getRewardRateAndIntroDriver(marketplaceEntity) {
  return [quickFactMappings.creditCards.getQuickFactRewardRate(marketplaceEntity), quickFactMappings.creditCards.getQuickFactIntroOffer(marketplaceEntity)];
};
/**
 * Get drivers for CC_BT_AND_REWARDS driver group
 * https://nerdwallet.atlassian.net/browse/SHOP-11146
 */


var getBTRoundupAndRewardsDrivers = function getBTRoundupAndRewardsDrivers(args) {
  return [].concat(_toConsumableArray(getBalanceTransferRoundupDrivers(args)), _toConsumableArray(getRewardRateAndIntroDriver(args.marketplaceEntity)));
};
/**
 * Get drivers for CC_BT_ROUNDUP_AND_REWARDS driver group
 * https://nerdwallet.atlassian.net/browse/SHOP-11146
 */


var getBTAndRewardsDrivers = function getBTAndRewardsDrivers(args) {
  return [].concat(_toConsumableArray(getBalanceTransferDrivers(args)), _toConsumableArray(getRewardRateAndIntroDriver(args.marketplaceEntity)));
};
/**
 * @enum {string}
 */


var RewardType = {
  CASH: 'Cash',
  MILES: 'Miles',
  POINTS: 'Points'
};
/**
 * Get drivers for CC_BONUS_OFFER_11606_VARIANT driver group
 * https://nerdwallet.atlassian.net/browse/SHOP-11606
 */

var getBonusValueVariantDrivers = function getBonusValueVariantDrivers(args) {
  var introOffer = quickFactMappings.creditCards.getQuickFactIntroOffer(args.marketplaceEntity);
  return introOffer.description === RewardType.CASH ? getRewardsDrivers(args) : [quickFactMappings.creditCards.getQuickFactAnnualFee(args.marketplaceEntity), quickFactMappings.creditCards.getQuickFactRewardRate(args.marketplaceEntity), quickFactMappings.creditCards.getQuickFactEstBonusValue(args.marketplaceEntity), getQuickFactLikelihoodOfApproval(args)];
};
/**
 * Define config of how to create drivers based off a driverGroup and other props
 */


var DRIVER_GROUP_CONFIG = (_DRIVER_GROUP_CONFIG = {}, _defineProperty(_DRIVER_GROUP_CONFIG, DEFAULT_DRIVER_GROUP, getRewardsDrivers), _defineProperty(_DRIVER_GROUP_CONFIG, DRIVER_GROUPS_CREDIT_CARDS.CC_CREDIT_SCORE_PILL, getNewCreditDriver), _defineProperty(_DRIVER_GROUP_CONFIG, DRIVER_GROUPS_CREDIT_CARDS.CC_CLASSIC_DEFAULT, getClassicDefaultDrivers), _defineProperty(_DRIVER_GROUP_CONFIG, DRIVER_GROUPS_CREDIT_CARDS.CC_BALANCE_TRANSFER, getBalanceTransferDrivers), _defineProperty(_DRIVER_GROUP_CONFIG, DRIVER_GROUPS_CREDIT_CARDS.CC_BUILD_CREDIT, getBuildCreditDrivers), _defineProperty(_DRIVER_GROUP_CONFIG, DRIVER_GROUPS_CREDIT_CARDS.CC_REWARDS, getRewardsDrivers), _defineProperty(_DRIVER_GROUP_CONFIG, DRIVER_GROUPS_CREDIT_CARDS.CC_BALANCE_TRANSFER_LOA, getBalanceTransferWithLOADrivers), _defineProperty(_DRIVER_GROUP_CONFIG, DRIVER_GROUPS_CREDIT_CARDS.CC_BALANCE_TRANSFER_ROUNDUP, getBalanceTransferRoundupDrivers), _defineProperty(_DRIVER_GROUP_CONFIG, DRIVER_GROUPS_CREDIT_CARDS.CC_BT_AND_REWARDS, getBTAndRewardsDrivers), _defineProperty(_DRIVER_GROUP_CONFIG, DRIVER_GROUPS_CREDIT_CARDS.CC_BT_ROUNDUP_AND_REWARDS, getBTRoundupAndRewardsDrivers), _defineProperty(_DRIVER_GROUP_CONFIG, DRIVER_GROUPS_CREDIT_CARDS.CC_REWARDS_11741_VARIANT, getRewards11741VariantDrivers), _defineProperty(_DRIVER_GROUP_CONFIG, DRIVER_GROUPS_CREDIT_CARDS.CC_BONUS_OFFER_11606_VARIANT, getBonusValueVariantDrivers), _DRIVER_GROUP_CONFIG);
/**
 * Wrapper around getImageData which augments image data with lazy load information.
 * @param {MarketplaceEntityImageData} marketplaceEntity
 * @param {boolean} isKetoCard
 * @returns {MarketplaceEntityImageData}
 */

var getLazyLoadableImageData = function getLazyLoadableImageData(marketplaceEntity, isKetoCard) {
  var imageData = getImageData(marketplaceEntity, 'large');
  var imageDataMobile = getImageData(marketplaceEntity, 'medium');
  var imageDimensions = getImageDimensions(marketplaceEntity, 'large');
  var imageDimensionsMobile = getImageDimensions(marketplaceEntity, 'medium');
  var imageRawDimensions = getImageRawDimensions(marketplaceEntity);
  return getLoadableImageData({
    imageData: imageData,
    imageDataMobile: imageDataMobile,
    imageDimensions: imageDimensions,
    imageDimensionsMobile: imageDimensionsMobile,
    imageRawDimensions: imageRawDimensions,
    isActive: isActive(marketplaceEntity),
    isKetoCard: isKetoCard
  });
};

export default (function (_ref3) {
  var _analytics$productImp, _analytics$productImp2, _analytics$productImp3, _analytics$productImp4, _analytics$productImp5, _analytics$productImp6, _analytics$productImp7, _analytics$productImp8;

  var approvalOddsEnabled = _ref3.approvalOddsEnabled,
      analytics = _ref3.analytics,
      driverGroup = _ref3.driverGroup,
      enableAnalytics = _ref3.enableAnalytics,
      identity = _ref3.identity,
      isKetoCard = _ref3.isKetoCard,
      marketplaceEntity = _ref3.marketplaceEntity,
      omitRating = _ref3.omitRating,
      productType = _ref3.productType,
      reviewLinkHref = _ref3.reviewLinkHref,
      shouldLogImpression = _ref3.shouldLogImpression,
      shouldDisplayOurTakeHighlight = _ref3.shouldDisplayOurTakeHighlight,
      showMatchScoreCTA = _ref3.showMatchScoreCTA,
      showNotYetRated = _ref3.showNotYetRated,
      starRating = _ref3.starRating,
      taxonomy = _ref3.taxonomy,
      trkPageType = _ref3.trkPageType,
      trkProductPosition = _ref3.trkProductPosition;
  var marketingBullets = getMarketingBullets(marketplaceEntity);
  var termsFeesAndLink = getTermsAndFeesLink(marketplaceEntity);
  var isNonMonetizingOffer = isNonMonetizing(marketplaceEntity);
  var impressionContext = useContext(ImpressionContext);
  var secondaryCtaProps = {};

  if (!isNonMonetizingOffer) {
    secondaryCtaProps.secondaryCtaText = 'Rates & Fees';
    secondaryCtaProps.secondaryCtaHref = termsFeesAndLink;
  }

  var drivers = getDrivers({
    analytics: analytics,
    approvalOddsEnabled: approvalOddsEnabled,
    driverGroupConfig: DRIVER_GROUP_CONFIG,
    driverGroup: driverGroup,
    enableAnalytics: enableAnalytics,
    identity: identity,
    isKetoCard: isKetoCard,
    marketplaceEntity: marketplaceEntity,
    showMatchScoreCTA: showMatchScoreCTA,
    trkProductPosition: trkProductPosition,
    impressionContext: impressionContext
  });
  var matchScore = getMatchScore(marketplaceEntity);

  if (!omitRating && matchScore && isKetoCard && showMatchScoreCTA) {
    var args = {
      analytics: analytics,
      enableAnalytics: enableAnalytics || !_isEmpty(analytics),
      trkLocation: TRK_PRODUCT_LOCATION,
      trkPageType: trkPageType
    };
    var starRatingQuickFact;

    if (showNotYetRated) {
      starRatingQuickFact = quickFactMappings.creditCards.getQuickFactStarRating({}, _objectSpread(_objectSpread({}, args), {}, {
        showNotYetRated: showNotYetRated
      }));
    } else {
      starRatingQuickFact = quickFactMappings.creditCards.getQuickFactStarRating(marketplaceEntity, _objectSpread(_objectSpread({}, args), {}, {
        productType: productType,
        starRating: starRating,
        ratingTextHref: reviewLinkHref,
        trkTaxonomy: taxonomy
      }));
    }

    drivers = [starRatingQuickFact].concat(_toConsumableArray(drivers));
  }

  return _objectSpread({
    drivers: drivers,
    ourTakeHighlight: shouldDisplayOurTakeHighlight ? findOurTakeHighlightContent(marketingBullets) : null,
    productDetails: !_isEmpty(marketingBullets) ? /*#__PURE__*/React.createElement(List, null, marketingBullets.map(function (productDetail) {
      return /*#__PURE__*/React.createElement(Li, {
        key: "product-detail-".concat(_kebabCase(productDetail)),
        className: classes('margin-bottom-2')
      }, sanitizeNode(productDetail, true));
    }), !isNonMonetizingOffer && termsFeesAndLink !== null && !isKetoCard ? /*#__PURE__*/React.createElement(Li, null, /*#__PURE__*/React.createElement(MarketplaceApplyNow, {
      className: styles.secondaryCtaProductDetails,
      finishType: APPLY_NOW_FINISH_TYPES.EXTERNAL_CONTENT,
      id: APPLY_NOW_LINK_TYPES.ratesAndFeesText,
      impressionId: impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId,
      link: termsFeesAndLink,
      linkType: APPLY_NOW_LINK_TYPES.ratesAndFeesText,
      monetizable: getMonetizable(marketplaceEntity),
      marketplaceEntity: marketplaceEntity,
      sectionName: (analytics === null || analytics === void 0 ? void 0 : (_analytics$productImp = analytics.productImpressionProps) === null || _analytics$productImp === void 0 ? void 0 : (_analytics$productImp2 = _analytics$productImp.payload) === null || _analytics$productImp2 === void 0 ? void 0 : _analytics$productImp2.section_name) || "marketplace_product_card",
      sectionPosition: analytics === null || analytics === void 0 ? void 0 : (_analytics$productImp3 = analytics.productImpressionProps) === null || _analytics$productImp3 === void 0 ? void 0 : (_analytics$productImp4 = _analytics$productImp3.payload) === null || _analytics$productImp4 === void 0 ? void 0 : _analytics$productImp4.section_position,
      type: LINK_TYPES.LINK,
      shouldLogImpression: shouldLogImpression
    }, "View Rates and Fees")) : null, !isNonMonetizingOffer && termsFeesAndLink !== null && isKetoCard ? /*#__PURE__*/React.createElement(Li, null, /*#__PURE__*/React.createElement(MarketplaceApplyNow, {
      className: styles.secondaryCta,
      finishType: APPLY_NOW_FINISH_TYPES.EXTERNAL_CONTENT,
      id: APPLY_NOW_LINK_TYPES.ratesAndFeesText,
      impressionId: impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId,
      link: termsFeesAndLink,
      linkType: APPLY_NOW_LINK_TYPES.ratesAndFeesText,
      monetizable: getMonetizable(marketplaceEntity),
      marketplaceEntity: marketplaceEntity,
      sectionName: (analytics === null || analytics === void 0 ? void 0 : (_analytics$productImp5 = analytics.productImpressionProps) === null || _analytics$productImp5 === void 0 ? void 0 : (_analytics$productImp6 = _analytics$productImp5.payload) === null || _analytics$productImp6 === void 0 ? void 0 : _analytics$productImp6.section_name) || "marketplace_product_card",
      sectionPosition: analytics === null || analytics === void 0 ? void 0 : (_analytics$productImp7 = analytics.productImpressionProps) === null || _analytics$productImp7 === void 0 ? void 0 : (_analytics$productImp8 = _analytics$productImp7.payload) === null || _analytics$productImp8 === void 0 ? void 0 : _analytics$productImp8.section_position,
      type: LINK_TYPES.LINK,
      shouldLogImpression: shouldLogImpression
    }, "View Rates and Fees")) : null) : null,
    imageData: getLazyLoadableImageData(marketplaceEntity, isKetoCard),
    reviewLinkTabIndex: -1
  }, secondaryCtaProps);
});